import {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {Button, IconButton, Typography} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  CreateScheduleDialog,
  Currency,
  CurrencyEventSchedule,
  formatEventTemplateType,
  useDeleteCurrencyEventSchedule,
  useGetCurrencyEventSchedules,
} from '@entities/currency';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {Header} from '@widgets/common';

import {PaginationState} from '@/shared/types';
import {COLOR__WHITE} from '@/theme/colors';

const scheduleColumns: MRT_ColumnDef<CurrencyEventSchedule>[] = [
  {
    accessorKey: 'templateType',
    header: 'Template type',
    enableSorting: false,
    Cell: ({row}) => formatEventTemplateType(row.original.templateType),
  },
  {
    accessorKey: 'cron',
    header: 'Cron',
    enableSorting: false,
  },
  {
    accessorKey: 'nextRunAt',
    header: 'Next run at',
    Cell: ({row}) => formatInTz(row.original.nextRunAt, DATETIME_FORMAT),
  },
];

export const CurrencyDetailsPage = () => {
  const {currency_id} = useParams<{currency_id: string}>();
  const location = useLocation();
  const currency = (location.state as {currency?: Currency} | null)?.currency;
  const {enqueueSnackbar} = useSnackbar();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {data, isPending, error} = useGetCurrencyEventSchedules(
    {
      currencyId: currency_id,
      page: urlState.pageIndex + 1,
      pageSize: urlState.pageSize,
    },
    {enabled: !!currency_id},
  );

  const {mutate: deleteSchedule} = useDeleteCurrencyEventSchedule();

  const handleDelete = async (schedule: CurrencyEventSchedule) => {
    try {
      await openConfirmation({
        title: 'Delete schedule',
        content: 'Are you sure you want to delete this schedule?',
        okButtonText: 'Delete',
      });
    } catch {
      return;
    }

    deleteSchedule(schedule.id, {
      onSuccess: () => {
        enqueueSnackbar('Schedule deleted successfully', {
          variant: 'success',
        });
      },
      onError: err => {
        const errorMessage = isAxiosError(err)
          ? getErrorMessageFromApiError(err)
          : '';
        enqueueSnackbar(`Could not delete schedule. ${errorMessage}`, {
          variant: 'error',
        });
      },
    });
  };

  if (!currency_id) {
    return 'Invalid currency ID';
  }

  return (
    <>
      <Header title={currency?.slug ?? `Currency ${currency_id}`} mb={1} />

      <CardBox bgcolor={COLOR__WHITE} mb={3}>
        <Typography>
          <strong>ID:</strong> {currency_id}
        </Typography>
        {currency?.slug && (
          <Typography>
            <strong>Slug:</strong> {currency.slug}
          </Typography>
        )}
      </CardBox>

      <CardBox bgcolor={COLOR__WHITE}>
        <Header title="Schedules" mb={2} isBackButtonShown={false} />

        <MrTable
          columns={scheduleColumns}
          data={data?.items ?? []}
          error={error}
          renderTopToolbarCustomActions={() => (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              Create schedule
            </Button>
          )}
          renderRowActions={({row}) => (
            <TableRowActionsContainer>
              <IconButton
                size="small"
                onClick={() => void handleDelete(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </TableRowActionsContainer>
          )}
          enablePagination
          manualPagination
          rowCount={data?.total ?? 0}
          onPaginationChange={setUrl}
          state={{
            isLoading: isPending,
            pagination: urlState,
          }}
          {...entityTableCommonProps}
        />
      </CardBox>

      <CreateScheduleDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        currencyId={currency_id}
      />
    </>
  );
};
